/**
 * FileField component for handling document field rendering in workflow forms
 * Supports the new normalized document field structure with nested "target" object
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Typography, 
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { logger } from '../../utils/logger';

// Styled components using MUI v5 styled API
const Root = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: '100%'
}));

const PreviewContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  position: 'relative',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper
}));

const FileInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: theme.spacing(1)
}));

const FileName = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  marginBottom: theme.spacing(0.5)
}));

const FileSize = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary
}));

const ActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1)
}));

const UploadButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1)
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(1)
}));

// Additional styled components
const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1)
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  marginTop: theme.spacing(1)
}));

const PreviewImage = styled('img')(({ theme }) => ({
  maxWidth: '100%',
  maxHeight: '200px',
  marginTop: theme.spacing(1),
  borderRadius: theme.shape.borderRadius
}));

const PreviewPdf = styled('iframe')(({ theme }) => ({
  width: '100%',
  height: '300px',
  border: 'none',
  marginTop: theme.spacing(1)
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  zIndex: 1
}));

/**
 * Extract document URL from field value, handling the new normalized structure
 * 
 * @param {any} value - The field value which might be a string or object
 * @returns {string} - The extracted document URL
 */
const extractDocumentUrl = (value) => {
  if (!value) return '';
  
  // Handle the new normalized document field structure with nested "target" object
  if (typeof value === 'object' && value !== null) {
    if (value.target && typeof value.target === 'object') {
      // Extract URL from target.value.url or target.url
      if (value.target.value && typeof value.target.value === 'object') {
        return value.target.value.url || value.target.value.s3Url || value.target.value.downloadUrl || '';
      }
      return value.target.url || '';
    } else if (value.url) {
      // Extract URL directly from value.url
      return value.url || '';
    } else if (value.s3Url) {
      return value.s3Url || '';
    } else if (value.downloadUrl) {
      return value.downloadUrl || '';
    } else {
      // Log the unexpected object structure
      logger.debug('Document field has object value but no recognized URL structure', { value });
      return '';
    }
  } else {
    // Handle string values (old format or simple URL strings)
    // Ensure we always return a string, not an object
    return typeof value === 'string' ? value : '';
  }
};

/**
 * Extract document name from field value or URL
 * 
 * @param {any} value - The field value
 * @param {string} url - The document URL
 * @returns {string} - The document name
 */
const extractDocumentName = (value, url) => {
  if (!value && !url) return '';
  
  // Try to get name from object structure first
  if (typeof value === 'object' && value !== null) {
    if (value.target && typeof value.target === 'object') {
      if (value.target.value && typeof value.target.value === 'object') {
        if (value.target.value.originalName || value.target.value.fileName) {
          return value.target.value.originalName || value.target.value.fileName;
        }
      }
      return value.target.name || '';
    } else if (value.originalName || value.fileName) {
      return value.originalName || value.fileName;
    } else if (value.name) {
      return value.name;
    }
  }
  
  // Fall back to extracting from URL
  if (url) {
    const urlParts = url.split('/');
    return urlParts[urlParts.length - 1];
  }
  
  return '';
};

/**
 * Get file type from URL
 * 
 * @param {string} url - The document URL
 * @returns {string} - The file type (image, pdf, or other)
 */
const getFileTypeFromUrl = (url) => {
  if (!url) return 'other';
  
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.endsWith('.jpg') || lowerUrl.endsWith('.jpeg') || 
      lowerUrl.endsWith('.png') || lowerUrl.endsWith('.gif')) {
    return 'image';
  } else if (lowerUrl.endsWith('.pdf')) {
    return 'pdf';
  }
  
  return 'other';
};

/**
 * Format file size for display
 * 
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  if (bytes === 0) return '0 Byte';
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
  return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
};

/**
 * FileField component for displaying and managing document fields
 * 
 * @param {Object} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const FileField = ({
  field,
  value,
  onChange,
  onUpload,
  onDelete,
  disabled,
  required,
  allowedFileTypes,
  maxFileSize
}) => {
  const [fileUrl, setFileUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileType, setFileType] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  
  // Extract document URL and metadata from field value
  useEffect(() => {
    const documentUrl = extractDocumentUrl(value);
    setFileUrl(documentUrl);
    
    if (documentUrl) {
      setFileName(extractDocumentName(value, documentUrl));
      const type = getFileTypeFromUrl(documentUrl);
      setFileType(type);
      
      // Extract file size if available in the value object
      if (value && typeof value === 'object') {
        // Try to get size from normalized structure
        if (value.target && value.target.size) {
          setFileSize(value.target.size);
        } else if (value.size) {
          setFileSize(value.size);
        }
      }
      
      // Auto-show preview for images
      if (type === 'image') {
        setShowPreview(true);
      }
    }
  }, [value]);
  
  // Handle file input change
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type if allowed types specified
    if (allowedFileTypes && allowedFileTypes.length > 0) {
      const fileExt = file.name.split('.').pop().toLowerCase();
      if (!allowedFileTypes.includes(fileExt)) {
        setError(`File type not allowed. Allowed types: ${allowedFileTypes.join(', ')}`);
        return;
      }
    }
    
    // Validate file size if max size specified
    if (maxFileSize && file.size > maxFileSize) {
      setError(`File too large. Maximum size: ${formatFileSize(maxFileSize)}`);
      return;
    }
    
    setError('');
    setIsUploading(true);
    setUploadProgress(0);
    
    // Upload file if onUpload handler provided
    if (onUpload) {
      onUpload(file, {
        onProgress: (progress) => setUploadProgress(progress),
        onSuccess: (response) => {
          // Extract URL from response
          const url = response.url || response.s3Url || response.downloadUrl || '';
          
          setFileUrl(url);
          setFileName(file.name);
          setFileType(getFileTypeFromUrl(url));
          setIsUploading(false);
          
          // Call onChange with the new value in the normalized format
          if (onChange) {
            onChange({
              target: {
                url: url,
                name: file.name,
                size: file.size,
                type: file.type,
                // Include any additional metadata from response
                s3Key: response.s3Key,
                s3Url: response.s3Url,
                downloadUrl: response.downloadUrl,
                ticketDocumentPath: response.ticketDocumentPath
              },
              hasFile: true,
              uploadStatus: 'COMPLETED'
            });
          }
        },
        onError: (err) => {
          setError(`Upload failed: ${err.message || 'Unknown error'}`);
          setIsUploading(false);
        }
      });
    }
  };
  
  // Handle view document
  const handleView = () => {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };
  
  // Handle download document
  const handleDownload = () => {
    if (fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  
  // Handle delete document
  const handleDelete = () => {
    if (onDelete) {
      onDelete(field);
    }
    
    setFileUrl('');
    setFileName('');
    setFileType('');
    setShowPreview(false);
    
    // Call onChange with empty value
    if (onChange) {
      onChange({
        target: {
          url: '',
          name: '',
          size: 0,
          type: ''
        },
        hasFile: false,
        uploadStatus: 'NONE'
      });
    }
  };
  
  // If no file uploaded yet, show upload interface
  if (!fileUrl) {
    return (
      <Root>
        <PreviewContainer elevation={0}>
          {isUploading && (
            <LoadingOverlay>
              <CircularProgress size={24} />
            </LoadingOverlay>
          )}
          
          <Typography variant="body1" gutterBottom>
            {field.title || field.name || 'Upload Document'}
            {required && <span style={{ color: 'red' }}> *</span>}
          </Typography>
          
          <input
            accept={allowedFileTypes ? allowedFileTypes.map(type => `.${type}`).join(',') : '*/*'}
            style={{ display: 'none' }}
            id={`file-input-${field.name || field.id || 'doc'}`}
            type="file"
            onChange={handleFileChange}
            disabled={disabled || isUploading}
          />
          
          <label htmlFor={`file-input-${field.name || field.id || 'doc'}`}>
            <UploadButton
              variant="contained"
              color="primary"
              component="span"
              startIcon={<UploadIcon />}
              disabled={disabled || isUploading}
            >
              Upload Document
            </UploadButton>
          </label>
          
          {error && (
            <ErrorText variant="body2">
              {error}
            </ErrorText>
          )}
          
          {isUploading && (
            <ProgressContainer>
              <Typography variant="body2">
                {uploadProgress}% Uploaded
              </Typography>
              <CircularProgress 
                variant="determinate" 
                value={uploadProgress} 
                size={24} 
              />
            </ProgressContainer>
          )}
        </PreviewContainer>
      </Root>
    );
  }
  
  // If file is uploaded, show preview and actions
  return (
    <Root>
      <PreviewContainer elevation={0}>
        {isUploading && (
          <LoadingOverlay>
            <CircularProgress size={24} />
          </LoadingOverlay>
        )}
        
        {!disabled && (
          <DeleteButton 
            size="small" 
            onClick={handleDelete}
            aria-label="Delete document"
          >
            <DeleteIcon fontSize="small" />
          </DeleteButton>
        )}
        
        {/* File info section */}
        <FileInfo>
          <FileName variant="subtitle1">
            {fileName}
          </FileName>
          
          {fileSize && (
            <FileSize variant="body2">
              {formatFileSize(fileSize)}
            </FileSize>
          )}
        </FileInfo>
        
        {/* Preview section */}
        {showPreview && (
          <>
            {fileType === 'image' && (
              <PreviewImage 
                src={fileUrl} 
                alt={fileName}
              />
            )}
            {fileType === 'pdf' && (
              <PreviewPdf 
                src={`${fileUrl}#toolbar=0`} 
                title={fileName}
              />
            )}
          </>
        )}
        
        {/* Action buttons */}
        <ActionButtons>
          {!showPreview && fileType === 'image' && (
            <Button 
              size="small" 
              onClick={() => setShowPreview(true)}
              startIcon={<VisibilityIcon />}
            >
              Show Preview
            </Button>
          )}
          
          <Tooltip title="View Document">
            <Button 
              size="small" 
              onClick={handleView}
              startIcon={<VisibilityIcon />}
            >
              View
            </Button>
          </Tooltip>
          
          <Tooltip title="Download Document">
            <Button 
              size="small" 
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
            >
              Download
            </Button>
          </Tooltip>
        </ActionButtons>
      </PreviewContainer>
    </Root>
  );
};

FileField.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  onChange: PropTypes.func,
  onUpload: PropTypes.func,
  onDelete: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  allowedFileTypes: PropTypes.arrayOf(PropTypes.string),
  maxFileSize: PropTypes.number
};

FileField.defaultProps = {
  disabled: false,
  required: false
};

export default FileField;
